import { useState } from "react";
import { Link } from "react-router-dom";
import { SITE } from "../config";
import HeroIllustration from "../components/HeroIllustration";

import { GridPattern, DotPattern } from "../components/Patterns";
import { usePageMeta } from "../seo";
import {
  InboxIcon,
  SparkleIcon,
  BagIcon,
  ArrowRightIcon,
  CheckIcon,
  BoltIcon,
  BookIcon,
  UsersIcon,
  ShieldIcon,
  PlugIcon,
  StopwatchIcon,
  ChartIcon,
  PlusMinusIcon,
} from "../components/Icons";

const featurePreview = [
  {
    title: "Email, WhatsApp and phone in one inbox",
    body:
      "Forward your support address, connect Gmail, Microsoft 365 or any IMAP mailbox, add a WhatsApp Business number, or connect Acefone for calls. Every conversation becomes a ticket on one timeline.",
    Icon: InboxIcon,
  },
  {
    title: "AI drafts with your own key",
    body:
      "Connect your OpenAI or Anthropic key and agents can draft a reply or summarize a long thread. The AI reads the conversation, the agent edits, and nothing is sent without them.",
    Icon: SparkleIcon,
  },
  {
    title: "Store context next to the ticket",
    body:
      "Shopify orders, fulfillment and lifetime value, your Zopping store, or fields from your own system through the customer data lookup. No tab-switching to answer \"where's my order?\"",
    Icon: BagIcon,
  },
  {
    title: "Rules for the routine work",
    body:
      "When a ticket arrives or a customer replies, match on channel, queue, sender, subject or message text, then assign, round-robin, pick the least busy agent, tag, set priority or close quietly.",
    Icon: BoltIcon,
  },
  {
    title: "Saved replies with variables",
    body:
      "Save the replies you send every day. {{customer.first_name}}, {{agent.name}}, {{ticket.subject}} and {{org.name}} fill themselves in when an agent inserts one.",
    Icon: BookIcon,
  },
  {
    title: "SLA targets, CSAT and reports",
    body:
      "Set first-reply and resolution targets by priority in business hours, send a one-tap satisfaction survey when a ticket is solved, and see volume, response times and SLA results by channel, queue and agent.",
    Icon: ChartIcon,
  },
];

const facts = [
  { value: "3", label: "Channels today", sub: "Email, WhatsApp (Meta Cloud API) and phone (Acefone)" },
  { value: "Free", label: "During the beta", sub: "Pricing announced before launch, with notice" },
  { value: "Your key", label: "For AI features", sub: "OpenAI or Anthropic, billed to your own account" },
  { value: "API", label: "For your own systems", sub: "REST API, signed webhooks, lookup and ticket actions" },
];

const deepDives = [
  {
    tag: "AI assist",
    Icon: SparkleIcon,
    title: "A first draft from the conversation. The agent decides what goes out.",
    body:
      "Each org connects its own OpenAI or Anthropic API key, so AI usage is billed to your account and you choose the model. Agents ask for a draft or a summary on a ticket; ZopDesk sends the conversation to your provider and puts the answer in the reply box to edit.",
    bullets: [
      "Draft a reply from the ticket's conversation",
      "Summarize a long thread before you pick it up",
      "Nothing is sent until an agent sends it",
      "A usage dashboard in settings shows what you've used",
    ],
    mockup: (
      <div className="rounded-lg border border-ink-200 bg-white p-4 text-xs">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold text-ink-900">Draft preview</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700">
            <SparkleIcon width={10} height={10} />
            From the conversation
          </span>
        </div>
        <div className="space-y-2 rounded-md bg-ink-50 p-3 text-ink-700">
          <p>Hi Priya, sorry the linen set arrived in the wrong size.</p>
          <p>We can send a replacement in the right size, or refund the order. Which would you prefer?</p>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="rounded-md bg-ink-100 px-2 py-1 text-ink-700">Edit before sending</span>
          <span className="rounded-md bg-ink-100 px-2 py-1 text-ink-700">Your OpenAI or Anthropic key</span>
        </div>
      </div>
    ),
  },
];

// Keep in step with the FAQPage JSON-LD in index.html.
const faq = [
  {
    q: "What does ZopDesk cost?",
    a: "Nothing during the beta. We'll announce pricing before launch and give every team using ZopDesk notice before anything changes. AI features use your own OpenAI or Anthropic key, so those calls are billed by your provider.",
  },
  {
    q: "How do I get started?",
    a: "Sign in with your Google account and name your workspace. Then connect a channel: forward your support address, connect a Gmail, Microsoft 365 or IMAP mailbox, add a WhatsApp Business number or connect Acefone. Invite your team from Settings.",
  },
  {
    q: "Which AI providers can I use?",
    a: "OpenAI or Anthropic, with your own API key. There is no shared ZopDesk AI account. Without a key, everything except drafts and summaries works as usual.",
  },
  {
    q: "What isn't there yet?",
    a: "Live chat, Messenger and Instagram, a customer portal and a knowledge base are on the roadmap but not available. There's no importer from other helpdesks, no SSO and no audit log yet.",
  },
];

const explorePages = [
  {
    to: "/integrations",
    eyebrow: "Integrations",
    title: "What connects today.",
    body: "Gmail, Microsoft 365, IMAP, WhatsApp, Acefone, Shopify, Zopping, OpenAI and Anthropic, plus an API and webhooks.",
    Icon: PlugIcon,
  },
  {
    to: "/security",
    eyebrow: "Security",
    title: "How your data is kept apart.",
    body: "Every row scoped to your org, credentials encrypted at rest, Google sign-in and four roles. And what we don't offer yet.",
    Icon: ShieldIcon,
  },
  {
    to: "/customers",
    eyebrow: "In use",
    title: "Built with Zopping's support team.",
    body: "Zopping's own support team runs on ZopDesk every day. Here's what they use.",
    Icon: UsersIcon,
  },
];

export default function Landing() {
  usePageMeta("/");
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="absolute inset-0 text-white/[0.04]">
          <GridPattern />
        </div>
        <div className="pointer-events-none absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-20 sm:px-6 sm:pt-24 sm:pb-28">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <p
                className="mb-4 inline-flex animate-fade-up items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-ink-200 backdrop-blur"
                style={{ animationDelay: "0ms" }}
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-70"></span>
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-400"></span>
                </span>
                In beta · free during the beta
              </p>
              <h1
                className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl md:text-6xl"
                style={{ animationDelay: "80ms" }}
              >
                Customer support,
                <br className="hidden sm:block" /> built for modern merchants.
              </h1>
              <p
                className="mt-6 max-w-2xl animate-fade-up text-lg leading-relaxed text-ink-300"
                style={{ animationDelay: "180ms" }}
              >
                {SITE.productName} is a helpdesk for online stores. Email,
                WhatsApp messages and phone calls arrive as tickets in one
                shared inbox, with the customer's Shopify or store orders beside
                them, rules that assign and tag, and AI drafts from your own
                OpenAI or Anthropic key.
              </p>
              <div
                className="mt-8 flex animate-fade-up flex-wrap gap-3"
                style={{ animationDelay: "280ms" }}
              >
                <a
                  href={SITE.appUrl}
                  className="group inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400 hover:shadow-xl hover:shadow-brand-500/40"
                >
                  Start free
                  <ArrowRightIcon
                    width={16}
                    height={16}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </a>
                <Link
                  to="/features"
                  className="rounded-md border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-ink-100 backdrop-blur transition hover:border-white/25 hover:bg-white/10"
                >
                  See features
                </Link>
              </div>
              <div
                className="mt-8 flex animate-fade-up flex-wrap items-center gap-5 text-xs text-ink-400"
                style={{ animationDelay: "360ms" }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <CheckIcon width={14} height={14} className="text-emerald-400" />
                  No credit card
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckIcon width={14} height={14} className="text-emerald-400" />
                  Sign in with Google
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckIcon width={14} height={14} className="text-emerald-400" />
                  Bring your own AI key
                </span>
              </div>
            </div>
            <div
              className="relative animate-fade-in"
              style={{ animationDelay: "200ms" }}
            >
              <HeroIllustration />
            </div>
          </div>
        </div>

      </section>


      <section className="border-b border-ink-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {facts.map((m, i) => (
              <div
                key={m.label}
                className="animate-fade-up border-l-2 border-brand-500 pl-5"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <p className="text-4xl font-semibold tracking-tight text-ink-900">
                  {m.value}
                </p>
                <p className="mt-2 text-sm font-medium text-ink-900">{m.label}</p>
                <p className="mt-1 text-xs text-ink-500">{m.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mb-12 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
            The platform
          </p>
          <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            What your support team gets today.
          </h2>
          <p className="mt-4 text-lg text-ink-600">
            Everything below is built and in daily use. Sign in with Google,
            connect a channel and invite your team.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featurePreview.map(({ title, body, Icon }, i) => (
            <div
              key={title}
              className="group relative animate-fade-up overflow-hidden rounded-xl border border-ink-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                <Icon width={22} height={22} />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-ink-900">
                {title}
              </h3>
              <p className="text-sm leading-relaxed text-ink-600">{body}</p>
              <div className="pointer-events-none absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-brand-500/5 blur-2xl transition group-hover:bg-brand-500/10" />
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link
            to="/features"
            className="inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:text-brand-600"
          >
            Browse every feature
            <ArrowRightIcon width={14} height={14} />
          </Link>
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <GridPattern />
        </div>
        <div className="pointer-events-none absolute -top-32 left-1/3 h-96 w-96 rounded-full bg-brand-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 right-0 h-80 w-80 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-300">
              How it works
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              From inbound message to resolution — same loop, every time.
            </h2>
            <p className="mt-4 text-lg text-ink-300">
              The same four steps whether it's a refund question by email or a
              missed call on a Saturday.
            </p>
          </div>
          <div className="relative">
            <div
              className="pointer-events-none absolute top-[52px] z-0 hidden h-px md:block"
              style={{ left: "12.5%", right: "12.5%" }}
            >
              <div className="h-full w-full bg-gradient-to-r from-transparent via-brand-400/30 to-transparent" />
            </div>

            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  step: "01",
                  title: "Ingest",
                  body: "Emails, WhatsApp messages and calls become tickets. A customer's reply lands on the same ticket, matched by the email thread or their phone number.",
                  icon: (
                    <svg viewBox="0 0 64 64" fill="none" className="h-12 w-12">
                      <rect x="10" y="22" width="44" height="28" rx="3" stroke="#818cf8" strokeWidth="1.8" />
                      <path d="M10 25l22 13 22-13" stroke="#818cf8" strokeWidth="1.8" fill="none" strokeLinejoin="round" />
                      <path d="M32 6v12" stroke="#a5b4fc" strokeWidth="1.8" strokeLinecap="round" />
                      <path d="M27 14l5 5 5-5" stroke="#a5b4fc" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                },
                {
                  step: "02",
                  title: "Route",
                  body: "Your rules assign, round-robin, tag or set priority as tickets arrive, and SLA due times follow the priority.",
                  icon: (
                    <svg viewBox="0 0 64 64" fill="none" className="h-12 w-12">
                      <path d="M12 14h24l12 18-12 18H12V14z" stroke="#818cf8" strokeWidth="1.8" strokeLinejoin="round" />
                      <circle cx="20" cy="32" r="2.5" fill="#c084fc" />
                      <circle cx="50" cy="18" r="3" fill="#f59e0b" opacity="0.7" />
                      <circle cx="50" cy="32" r="3" fill="#818cf8" opacity="0.7" />
                      <circle cx="50" cy="46" r="3" fill="#34d399" opacity="0.7" />
                      <line x1="36" y1="26" x2="46" y2="18" stroke="#a5b4fc" strokeWidth="1" opacity="0.4" />
                      <line x1="36" y1="32" x2="46" y2="32" stroke="#a5b4fc" strokeWidth="1" opacity="0.4" />
                      <line x1="36" y1="38" x2="46" y2="46" stroke="#a5b4fc" strokeWidth="1" opacity="0.4" />
                    </svg>
                  ),
                },
                {
                  step: "03",
                  title: "Draft",
                  body: "Insert a saved reply, or ask your AI provider for a draft from the conversation. The agent edits and sends.",
                  icon: (
                    <svg viewBox="0 0 64 64" fill="none" className="h-12 w-12">
                      <rect x="14" y="10" width="36" height="46" rx="3" stroke="#818cf8" strokeWidth="1.8" />
                      <line x1="22" y1="24" x2="42" y2="24" stroke="#a5b4fc" strokeWidth="1.8" strokeLinecap="round" opacity="0.6" />
                      <line x1="22" y1="32" x2="38" y2="32" stroke="#a5b4fc" strokeWidth="1.8" strokeLinecap="round" opacity="0.4" />
                      <line x1="22" y1="40" x2="34" y2="40" stroke="#a5b4fc" strokeWidth="1.8" strokeLinecap="round" opacity="0.3" />
                      <path d="M48 8l2 4 4 2-4 2-2 4-2-4-4-2 4-2z" fill="#c084fc" />
                      <path d="M8 52l1.5 3 3 1.5-3 1.5-1.5 3-1.5-3-3-1.5 3-1.5z" fill="#818cf8" opacity="0.5" />
                    </svg>
                  ),
                },
                {
                  step: "04",
                  title: "Resolve",
                  body: "Replies go out from your own mailbox or WhatsApp number. Solve the ticket and the customer gets a one-tap satisfaction survey.",
                  icon: (
                    <svg viewBox="0 0 64 64" fill="none" className="h-12 w-12">
                      <circle cx="32" cy="32" r="22" stroke="#818cf8" strokeWidth="1.8" />
                      <path d="M21 32l8 8 14-14" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M32 4a28 28 0 0 1 20 8" stroke="#c084fc" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
                      <path d="M58 22a28 28 0 0 1 2 10" stroke="#c084fc" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
                    </svg>
                  ),
                },
              ].map((s, i) => (
                <div
                  key={s.step}
                  className="group animate-fade-up text-center"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="relative mx-auto mb-5 flex h-[104px] w-[104px] items-center justify-center">
                    <div className="absolute inset-2 rounded-2xl bg-gradient-to-br from-brand-500/15 to-violet-500/15 blur-xl transition group-hover:from-brand-500/25 group-hover:to-violet-500/25" />
                    <div className="relative flex h-full w-full items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur transition group-hover:border-white/20 group-hover:bg-white/[0.07]">
                      {s.icon}
                    </div>
                  </div>
                  <span className="mb-2 inline-block bg-gradient-to-br from-brand-300 to-violet-300 bg-clip-text text-[11px] font-bold tracking-widest text-transparent">
                    STEP {s.step}
                  </span>
                  <h3 className="mb-2 text-lg font-semibold text-white">
                    {s.title}
                  </h3>
                  <p className="mx-auto max-w-[240px] text-sm leading-relaxed text-ink-300">
                    {s.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="space-y-24">
          {deepDives.map((d, i) => (
            <div
              key={d.tag}
              className={`grid items-center gap-10 md:grid-cols-2 ${
                i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-100">
                  <d.Icon width={12} height={12} />
                  {d.tag}
                </span>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
                  {d.title}
                </h2>
                <p className="mt-4 text-lg text-ink-600">{d.body}</p>
                <ul className="mt-6 space-y-3">
                  {d.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-ink-700">
                      <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                        <CheckIcon width={12} height={12} strokeWidth={2.4} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <div className="pointer-events-none absolute -inset-4 -z-10 rounded-2xl bg-gradient-to-br from-brand-500/10 to-indigo-500/10 blur-2xl" />
                <div className="rounded-2xl border border-ink-200 bg-white p-3 shadow-sm">
                  <div className="rounded-xl bg-ink-50 p-3">{d.mockup}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <DotPattern />
        </div>
        <div className="pointer-events-none absolute top-0 left-1/4 h-96 w-96 rounded-full bg-brand-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-300">
              Explore
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Want to go deeper?
            </h2>
            <p className="mt-3 text-ink-300">
              The questions you'll have if ZopDesk is on your shortlist.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {explorePages.map(({ to, eyebrow, title, body, Icon }, i) => (
              <Link
                key={to}
                to={to}
                className="group flex animate-fade-up flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.07]"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-md shadow-brand-500/30">
                  <Icon width={20} height={20} />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-300">
                  {eyebrow}
                </p>
                <h3 className="mt-2 text-lg font-semibold text-white">
                  {title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-300">
                  {body}
                </p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-300 transition group-hover:gap-2 group-hover:text-brand-200">
                  Learn more
                  <ArrowRightIcon width={14} height={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="grid items-center gap-8 rounded-2xl border border-ink-200 bg-white p-6 shadow-sm sm:p-10 md:grid-cols-[1.4fr_1fr]">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              Where ZopDesk is today
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              In beta, and used every day by Zopping's own support team.
            </h2>
            <p className="mt-4 text-ink-600">
              We're the team behind Zopping, and our support team answers
              merchants from ZopDesk. What they need shapes what we build next.
              It's early: some things you'd expect from an established helpdesk
              aren't here yet, and we say which on every page.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/customers"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-ink-200 px-5 py-3 text-sm font-semibold text-ink-900 transition hover:bg-ink-50"
            >
              How Zopping's team uses it
              <ArrowRightIcon width={14} height={14} />
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-ink-200 px-5 py-3 text-sm font-semibold text-ink-900 transition hover:bg-ink-50"
            >
              What's built and what isn't
              <ArrowRightIcon width={14} height={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
            FAQ
          </p>
          <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            Questions we hear most.
          </h2>
        </div>
        <div className="divide-y divide-ink-200 overflow-hidden rounded-xl border border-ink-200 bg-white">
          {faq.map((f, i) => (
            <FaqItem key={f.q} index={i} {...f} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <div className="relative overflow-hidden rounded-2xl border border-ink-200 bg-ink-900 p-10 text-center shadow-sm sm:p-14">
          <div className="absolute inset-0 text-white/[0.04]">
            <DotPattern />
          </div>
          <div className="pointer-events-none absolute -top-20 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-brand-500/20 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Try it with your own support inbox.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-ink-300">
              Free during the beta. Sign in with Google, connect a channel and
              answer your first ticket.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={SITE.appUrl}
                className="rounded-md bg-white px-5 py-3 text-sm font-semibold text-ink-900 transition hover:bg-ink-100"
              >
                Start free
              </a>
              <Link
                to="/contact"
                className="rounded-md border border-ink-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-ink-800"
              >
                Talk to us
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-ink-400">
              <span className="inline-flex items-center gap-1.5">
                <StopwatchIcon width={14} height={14} />
                Free during the beta
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldIcon width={14} height={14} />
                Every row scoped to your org
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ChartIcon width={14} height={14} />
                No credit card
              </span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left transition hover:bg-ink-50"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-ink-900">{q}</span>
        <span
          className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-full border border-ink-200 text-ink-600 transition ${
            open ? "rotate-45 border-brand-200 bg-brand-50 text-brand-700" : ""
          }`}
        >
          <PlusMinusIcon width={14} height={14} />
        </span>
      </button>
      <div
        className={`grid overflow-hidden px-6 transition-all duration-300 ease-out ${
          open ? "grid-rows-[1fr] pb-5" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="max-w-3xl text-sm leading-relaxed text-ink-600">{a}</p>
        </div>
      </div>
    </div>
  );
}


